import { makePath, matchPath, truncateHex } from '@lfg/core/lib';
import { Button, Menu, MenuHandles } from '@lfg/ui/components';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useAccount, useDisconnect } from 'wagmi';

import Link from 'src/components/Link';
import { colors } from 'src/global/styles/colors';
import { useGetAdminWalletQuery } from 'src/store/services/project.service';

const Header = () => {
  const [open, setOpen] = useState<boolean>(false);
  const menuRef = useRef<MenuHandles>(null);

  const { pathname } = useLocation();
  const theme = useTheme();

  const { disconnect } = useDisconnect();
  const { address } = useAccount();
  const { openConnectModal } = useConnectModal();

  const { data: adminWallet } = useGetAdminWalletQuery({ address: address as string }, { skip: !address });

  const baseNavs = useMemo(() => {
    return [
      { label: 'Core Sales', path: '/core-sale' },
      { label: 'Analytics', path: '/analytics', hide: true },
    ];
  }, [adminWallet, address]);

  const isHomePage = useMemo(() => pathname === '/', [pathname]);

  const handleSignOut = () => {
    disconnect();
  };

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      menuRef.current?.open(event);
    },
    [menuRef],
  );

  const onChangeMenuOpen = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
  }, []);

  const handleConnectWallet = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!address) {
        return openConnectModal?.();
      }

      return handleOpenMenu(event);
    },
    [address, openConnectModal, handleOpenMenu],
  );

  const connectWalletLabel = useMemo(() => {
    return address ? truncateHex(address, 4) : 'Connect Wallet';
  }, [address]);

  const renderNav = useCallback(() => {
    return baseNavs
      .filter(nav => !nav.hide)
      .map(nav => {
        const isActive = matchPath(pathname, makePath(`${nav.path}{/*path}`));

        return (
          <Link
            key={nav.label}
            to={nav.path}
            typographyProps={{
              variant: 'title1',
              textTransform: 'capitalize',
              fontWeight: isActive ? '700' : '400',
              color: isActive ? 'white' : colors.neutral90,
              sx: {
                '&:hover': {
                  color: 'customTab.active',
                },
              },
            }}>
            {nav.label}
          </Link>
        );
      });
  }, [pathname, baseNavs]);

  return (
    <Stack
      paddingY={3}
      zIndex={1300}
      width={'100%'}
      border={`1px solid ${colors.neutral015}`}
      borderLeft={'none'}
      borderRight={'none'}
      component={'header'}
      alignItems={'center'}
      justifyContent={'center'}>
      <Stack
        width={'min(1344px, 100%)'}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        paddingX={{ xl: 0, xs: 2 }}>
        <Stack
          width={'100%'}
          gap={{ xs: 2, lg: 7 }}
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}>
          <Stack
            gap={{ xs: 2, lg: 7 }}
            direction={'row'}
            alignItems={'center'}
            component={'a'}
            href={window.location.origin}>
            <img className={'h-10 md:h-[56px] w-auto'} src={'/logo.svg'} alt='Logo' />
          </Stack>
          {!isHomePage && (
            <Stack gap={{ xs: 0, md: 2 }} direction={'row'} alignItems={'center'}>
              <Stack marginRight={{ xs: 0, md: 8 }} sx={{ display: { sm: 'none', md: 'flex' } }}>
                <Stack gap={6} direction={'row'} alignItems={'center'} display={{ xs: 'none', sm: 'flex' }}>
                  {renderNav()}
                </Stack>
              </Stack>
              <Button
                id={'wallet-button'}
                bgColor={colors.neutral06}
                onClick={handleConnectWallet}
                borderRadius={'4px'}
                aria-expanded={open ? 'true' : undefined}
                aria-controls={open ? 'wallet-button-menu' : undefined}
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    padding: '0 2px 0 4px !important',
                  },
                  [theme.breakpoints.up('sm')]: {
                    padding: '0 16px 0 16px !important',
                  },
                  '&:hover': {
                    backgroundColor: '#31343b',
                  },
                }}>
                <Stack gap={1} alignItems={'center'} direction={'row'}>
                  <Typography
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        fontSize: '1rem',
                      },
                      [theme.breakpoints.up('sm')]: {
                        fontSize: '18px',
                      },
                    }}
                    fontWeight={'500'}
                    color={'white'}
                    whiteSpace={'nowrap'}>
                    {connectWalletLabel}
                  </Typography>
                  {address && (
                    <KeyboardArrowDownIcon sx={{ width: '32px', height: '32px', padding: 0, color: 'white' }} />
                  )}
                </Stack>
              </Button>
              {address && (
                <Menu
                  ref={menuRef}
                  id={'wallet-button-menu'}
                  data={{ title: 'Sign Out', onClick: handleSignOut }}
                  bgColor={colors.blue200}
                  onChangeOpen={onChangeMenuOpen}
                  menuPaperStyle={{
                    maxHeight: '350px',
                    borderRadius: '4px',
                    backdropFilter: 'blur(50px)',
                    backgroundColor: colors.neutral06,
                  }}
                  menuItemProps={{
                    sx: {
                      fontWeight: '600',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#31343b',
                      },
                    },
                  }}
                  transformOrigin={{
                    vertical: -10,
                    horizontal: 'left',
                  }}
                />
              )}
              <MobileMenu>
                {address && (
                  <Stack gap={2} padding={4}>
                    {renderNav()}
                  </Stack>
                )}
              </MobileMenu>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

interface MobileMenuProps {
  children: React.ReactNode;
}

const MobileMenu = ({ children }: MobileMenuProps) => {
  const [initialPadding, setInitialPadding] = useState<string>('');
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>(false);

  const toggle = () => setIsDrawerOpened(prev => !prev);

  useEffect(() => {
    if (isDrawerOpened) {
      setInitialPadding(document.body.style.paddingRight);
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.paddingRight = `${scrollBarWidth}px`;

      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.paddingRight = initialPadding;
      };
    }
  }, [isDrawerOpened, initialPadding]);

  return (
    <Stack sx={{ display: { xs: 'contents', md: 'none' } }}>
      <IconButton onClick={toggle}>
        {isDrawerOpened ? (
          <CloseIcon fontSize={'large'} sx={{ color: 'white' }} />
        ) : (
          <MenuIcon fontSize={'large'} sx={{ color: 'white' }} />
        )}
      </IconButton>

      <Drawer
        anchor={'top'}
        hideBackdrop
        variant={'temporary'}
        open={isDrawerOpened}
        onClose={() => setIsDrawerOpened(false)}
        sx={{
          zIndex: 100,
          top: '153px',
        }}
        PaperProps={{
          sx: {
            marginTop: '153px',
            height: '100dvh',
            backgroundColor: colors.pageBg,
          },
        }}>
        {children}
      </Drawer>
    </Stack>
  );
};

export default Header;
